import { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useApp } from '../../context/AppContext';
import { useAuth } from '../../context/AuthContext';
import { useWebSocket } from '../../context/WebSocketContext';
import { useOffline } from '../../context/OfflineContext';
import { ordersAPI, roomsAPI, usersAPI, commonSpacesAPI } from '../../services/api';
import { getDateRelativeToDate } from '../../utils/helpers';
import { SUCCESS_MESSAGES } from '../../utils/constants';
import { SkeletonTable } from '../../design-system';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import CleaningOrdersTable from '../../components/orders/CleaningOrdersTable';
import StaffSummaryPanel from '../../components/orders/StaffSummaryPanel';

const CleaningOrdersDesktop = () => {
  const { user } = useAuth();
  const { subscribe } = useWebSocket();
  const { isOnline } = useOffline();
  const navigate = useNavigate();
  const location = useLocation();
  const {
    selectedDate,
    setSelectedDate,
    rooms,
    setRooms,
    orders,
    setOrders,
    users,
    setUsers,
    filters,
    addNotification,
    getCleaningStaff,
    getRoomOrders
  } = useApp();

  const [commonSpaces, setCommonSpaces] = useState([]);

  const [isLoading, setIsLoading] = useState(true);
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [roomOrders, setRoomOrders] = useState({});
  const [tableHasChanges, setTableHasChanges] = useState(false);
  const [showNavigationWarning, setShowNavigationWarning] = useState(false);
  const [pendingNavigationPath, setPendingNavigationPath] = useState(null);

  const tableRef = useRef(null);

  // Warn user before closing/refreshing browser tab with unsaved changes
  useEffect(() => {
    const handleBeforeUnload = (e) => {
      if (tableHasChanges) {
        e.preventDefault();
        e.returnValue = ''; // Required for Chrome
        return ''; // Required for some browsers
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [tableHasChanges]);

  // Custom navigation blocker using click interceptor on navigation links
  useEffect(() => {
    const handleClick = (e) => {
      // Check if click was on a navigation link
      const target = e.target.closest('a[href], button[type="button"]');

      if (!target) return;

      // Check if it's a navigation link (not external)
      const href = target.getAttribute('href');
      if (!href || href.startsWith('http') || href.startsWith('mailto:') || href.startsWith('tel:')) {
        return;
      }

      // If there are unsaved changes, prevent navigation and show warning
      if (tableHasChanges) {
        e.preventDefault();
        e.stopPropagation();

        // Show custom modal dialog
        setPendingNavigationPath(href);
        setShowNavigationWarning(true);
      }
    };

    document.addEventListener('click', handleClick, true);

    return () => {
      document.removeEventListener('click', handleClick, true);
    };
  }, [tableHasChanges, navigate]);

  useEffect(() => {
    loadData();
  }, [selectedDate]);

  // WebSocket event handler - Update specific order without full reload
  const handleOrderUpdate = useCallback((orderData) => {

    // Check if the order is for the selected date
    const orderDateStr = orderData.date; // Already in YYYY-MM-DD format from backend
    const selectedDateStr = typeof selectedDate === 'string'
      ? selectedDate
      : selectedDate.toISOString().split('T')[0];

    if (orderDateStr === selectedDateStr) {
      setOrders(prevOrders => {

        if (!Array.isArray(prevOrders)) {
          console.error('prevOrders is not an array! Initializing as empty array');
          prevOrders = [];
        }

        const updatedOrders = [...prevOrders];
        const existingOrderIndex = updatedOrders.findIndex(o => o.id === orderData.id);

        // Transform WebSocket data to match the format expected by the app
        const transformedOrder = {
          ...orderData,
          // Ensure timestamps are properly converted
          checkout_timestamp: orderData.checkout_timestamp,
          cleaning_timestamp: orderData.cleaning_timestamp,
        };

        if (existingOrderIndex >= 0) {
          // Update existing order
          updatedOrders[existingOrderIndex] = transformedOrder;
        } else {
          // Add new order if it doesn't exist
          updatedOrders.push(transformedOrder);
        }

        return updatedOrders;
      });
    }
  }, [selectedDate, setOrders]);

  // WebSocket event handler for order deletion
  const handleOrderDeletedEvent = useCallback((orderData) => {

    if (!isSaving && orderData) {
      setOrders(prevOrders => prevOrders.filter(o => o.id !== orderData.id));
    }
  }, [isSaving, setOrders]);

  // Subscribe to WebSocket events
  useEffect(() => {
    const unsubscribeCreated = subscribe('order_created', handleOrderUpdate);
    const unsubscribeUpdated = subscribe('order_updated', handleOrderUpdate);
    const unsubscribeCheckoutCompleted = subscribe('checkout_completed', handleOrderUpdate);
    const unsubscribeCheckoutUnmarked = subscribe('checkout_unmarked', handleOrderUpdate);
    const unsubscribeCleaningCompleted = subscribe('cleaning_completed', handleOrderUpdate);
    const unsubscribeCleaningUnmarked = subscribe('cleaning_unmarked', handleOrderUpdate);
    const unsubscribeDeleted = subscribe('order_deleted', handleOrderDeletedEvent);

    return () => {
      unsubscribeCreated();
      unsubscribeUpdated();
      unsubscribeCheckoutCompleted();
      unsubscribeCheckoutUnmarked();
      unsubscribeCleaningCompleted();
      unsubscribeCleaningUnmarked();
      unsubscribeDeleted();
    };
  }, [subscribe, handleOrderUpdate, handleOrderDeletedEvent]);

  useEffect(() => {

    // Safety check: ensure orders is an array
    if (!Array.isArray(orders)) {
      console.error('Orders is not an array!', orders);
      return;
    }

    // Update room/space orders when orders change
    const ordersByRoom = {};
    orders.forEach(order => {
      // Use room_id or common_space_id with a prefix to differentiate
      const key = order.room_id ? `room-${order.room_id}` : `space-${order.common_space_id}`;

      if (!ordersByRoom[key]) {
        ordersByRoom[key] = [];
      }

      // Transform backend fields to frontend fields for consistency
      const transformedOrder = {
        ...order,
        // Map backend field names to frontend field names
        assigned_staff_id: order.cleaning_staff_id,
        bed_disposition_id: order.bed_disposition_id,
        bed_disposition_name: order.bed_disposition?.name || order.bed_disposition_name,
        cleaning_type_id: order.cleaning_type_id,
        cleaning_type_name: order.cleaning_type_obj?.name || order.cleaning_type_name,
        // Map timestamps to boolean values for UI
        checkout_status: order.checkout_timestamp !== null,
        cleaning_complete: order.cleaning_timestamp !== null,
        // Map cleaning_timestamp to status for frontend consistency
        status: order.cleaning_timestamp !== null ? 'completed' : 'pending'
      };

      // Add staff name to the order
      if (transformedOrder.assigned_staff_id && users.length > 0) {
        const staffMember = users.find(u => u.id === transformedOrder.assigned_staff_id);
        transformedOrder.assigned_staff_name = staffMember?.name || '';
      }

      ordersByRoom[key].push(transformedOrder);
    });
    setRoomOrders(ordersByRoom);
  }, [orders, users]);

  const loadData = async () => {
    try {
      // Mostrar spinner de pantalla completa solo en carga inicial
      if (isInitialLoad) {
        setIsLoading(true);
      } else {
        // Mostrar spinner sutil durante recarga
        setIsRefreshing(true);
      }

      const [roomsData, spacesData, ordersData, usersData] = await Promise.all([
        roomsAPI.getAll(),
        commonSpacesAPI.getAll(),
        ordersAPI.getByDate(selectedDate),
        usersAPI.getCleaningStaff()
      ]);

      setRooms(roomsData);
      setCommonSpaces(spacesData);
      setOrders(ordersData);
      setUsers(usersData);
    } catch (error) {
      console.error('Error loading data:', error);
      addNotification({
        type: 'error',
        message: 'Error al cargar los datos'
      });
    } finally {
      if (isInitialLoad) {
        setIsLoading(false);
        setIsInitialLoad(false);
      } else {
        setIsRefreshing(false);
      }
    }
  };

  const handleDateChange = (newDate) => {
    if (tableHasChanges) {
      const confirm = window.confirm('Tienes cambios sin guardar. ¿Deseas continuar sin guardar?');
      if (!confirm) return;
    }
    setSelectedDate(newDate);
    setTableHasChanges(false);
  };

  const handlePreviousDay = () => {
    const previousDate = getDateRelativeToDate(selectedDate, -1);
    handleDateChange(previousDate);
  };

  const handleNextDay = () => {
    const nextDate = getDateRelativeToDate(selectedDate, 1);
    handleDateChange(nextDate);
  };

  const handleCellChange = (rowIndex, colIndex) => {
    setCurrentCell({ row: rowIndex, col: colIndex });

    // Focus the cell
    setTimeout(() => {
      const cell = tableRef.current?.querySelector(
        `[data-row="${rowIndex}"][data-col="${colIndex}"]`
      );
      if (cell) {
        cell.focus();
      }
    }, 10);
  };

  const handleOrderChange = (itemKey, field, value, cleaningTypes = []) => {
    setTableHasChanges(true);

    const existingOrders = roomOrders[itemKey] || [];
    const mainOrder = existingOrders[0];

    if (field === 'hasOrder') {
      if (value && !mainOrder) {
        // Create new order
        const isRoom = itemKey.startsWith('room-');
        const itemId = parseInt(itemKey.split('-')[1]);

        let newOrder = {
          id: `temp-${Date.now()}`,
          date: selectedDate,
          // Only assign cleaning type to rooms, not common spaces
          cleaning_type_id: isRoom && cleaningTypes.length > 0 ? cleaningTypes[0].id : null,
          cleaning_type_name: isRoom && cleaningTypes.length > 0 ? cleaningTypes[0].name : null,
          assigned_staff_id: null,
          status: 'pending',
          notes: '',
          checkout_status: false,
          cleaning_complete: false,
          is_temp: true
        };

        if (isRoom) {
          const room = rooms.find(r => r.id === itemId);
          // Auto-select bed disposition based on room's available bed dispositions
          let bedDispositionId = null;
          if (room?.bed_dispositions && room.bed_dispositions.length > 0) {
            bedDispositionId = room.bed_dispositions[0].id;
          }
          newOrder.room_id = itemId;
          newOrder.bed_disposition_id = bedDispositionId;
        } else {
          // Common space
          newOrder.common_space_id = itemId;
        }

        setRoomOrders(prev => ({
          ...prev,
          [itemKey]: [newOrder]
        }));
      } else if (!value && mainOrder) {
        // Remove order
        setRoomOrders(prev => ({
          ...prev,
          [itemKey]: []
        }));
      }
    } else if (mainOrder) {
      // Update existing order
      const updatedOrder = { ...mainOrder, [field]: value };

      // Add staff name if assigning staff
      if (field === 'assigned_staff_id' && value) {
        const staffMember = users.find(u => u.id === parseInt(value));
        updatedOrder.assigned_staff_name = staffMember?.name || '';
      }

      setRoomOrders(prev => ({
        ...prev,
        [itemKey]: [updatedOrder, ...existingOrders.slice(1)]
      }));
    }
  };

  const handleOrderCreate = (roomId, orderData) => {
    setTableHasChanges(true);
    const room = rooms.find(r => r.id === roomId);
    const newOrder = {
      id: `temp-${Date.now()}`,
      room_id: roomId,
      date: selectedDate,
      cleaning_type_id: orderData.cleaning_type_id || null,
      cleaning_type_name: orderData.cleaning_type_name || 'Check-out',
      bed_disposition_id: orderData.bed_disposition_id || (room?.bed_dispositions?.[0]?.id || null),
      assigned_staff_id: orderData.assigned_staff_id || null,
      status: orderData.status || 'pending',
      notes: orderData.notes || '',
      checkout_status: false,
      cleaning_complete: false,
      is_temp: true
    };
    setRoomOrders(prev => ({
      ...prev,
      [roomId]: [newOrder]
    }));
  };

  const handleOrderDelete = (roomId, orderId) => {
    setTableHasChanges(true);
    setRoomOrders(prev => ({
      ...prev,
      [roomId]: []
    }));
  };

  const handleCheckoutStatusChange = async (itemKey, orderId, newCheckoutStatus) => {
    if (!orderId) return;

    try {
      const existingOrders = roomOrders[itemKey] || [];
      const mainOrder = existingOrders[0];

      if (!mainOrder) return;

      // Get item name for notification
      const isRoom = itemKey.startsWith('room-');
      const itemId = parseInt(itemKey.split('-')[1]);
      let itemName;

      if (isRoom) {
        const room = rooms.find(r => r.id === itemId);
        itemName = `habitación ${room?.number || itemId}`;
      } else {
        const space = commonSpaces.find(s => s.id === itemId);
        itemName = space?.name || `espacio ${itemId}`;
      }

      // Use specific checkout endpoint to mark/unmark checkout
      await ordersAPI.updateCheckout(orderId, newCheckoutStatus);

      // Update local state
      setRoomOrders(prev => {
        const existingOrders = prev[itemKey] || [];
        const mainOrder = existingOrders[0];

        if (mainOrder) {
          const updatedOrder = {
            ...mainOrder,
            checkout_status: newCheckoutStatus,
            checkout_timestamp: newCheckoutStatus ? new Date().toISOString() : null,
            // Auto-update status based on checkout and cleaning completion
            status: getOrderStatusFromStates(newCheckoutStatus, mainOrder.cleaning_complete)
          };

          return {
            ...prev,
            [itemKey]: [updatedOrder, ...existingOrders.slice(1)]
          };
        }
        return prev;
      });

      addNotification({
        type: 'success',
        message: `Check-out ${newCheckoutStatus ? 'marcado como realizado' : 'desmarcado'} para ${itemName}`
      });
    } catch (error) {
      console.error('Error updating checkout status:', error);
      addNotification({
        type: 'error',
        message: 'Error al actualizar el estado de check-out'
      });
    }
  };

  const handleCleaningStatusChange = (roomId, orderId, newCleaningStatus) => {
    setHasChanges(true);

    // Get room number for notification
    const room = rooms.find(r => r.id === roomId);
    const roomNumber = room?.number || roomId;

    setRoomOrders(prev => {
      const existingOrders = prev[roomId] || [];
      const mainOrder = existingOrders[0];

      if (mainOrder) {
        const updatedOrder = {
          ...mainOrder,
          cleaning_complete: newCleaningStatus,
          // Auto-update status based on checkout and cleaning completion
          status: getOrderStatusFromStates(mainOrder.checkout_status, newCleaningStatus)
        };

        return {
          ...prev,
          [roomId]: [updatedOrder, ...existingOrders.slice(1)]
        };
      }
      return prev;
    });

    addNotification({
      type: 'info',
      message: `Limpieza ${newCleaningStatus ? 'marcada como completada' : 'desmarcada como completada'} para habitación ${roomNumber}`
    });
  };

  const getOrderStatusFromStates = (isCheckedOut, isCleaningComplete) => {
    if (isCheckedOut && isCleaningComplete) {
      return 'completed';
    } else if (isCheckedOut || isCleaningComplete) {
      return 'in_progress';
    } else {
      return 'pending';
    }
  };

  // Helper functions to convert frontend boolean values to backend enum values
  const mapCheckoutStatusForBackend = (booleanValue) => {
    return booleanValue ? 'completado' : 'pendiente';
  };

  const mapCleaningStatusForBackend = (booleanValue) => {
    return booleanValue ? 'completado' : 'pendiente';
  };

  const handleSaveOrders = async () => {
    // Check connection before attempting to save
    if (!isOnline) {
      addNotification({
        type: 'warning',
        message: 'No hay conexión a Internet. Por favor, verifica tu conexión antes de guardar los cambios.'
      });
      return;
    }

    try {
      setIsSaving(true);

      const ordersToSave = [];
      const ordersToUpdate = [];
      const ordersToDelete = [];

      Object.entries(roomOrders).forEach(([itemKey, itemOrdersList]) => {
        const mainOrder = itemOrdersList[0];

        // Determine if this is a room or common space
        const isRoom = itemKey.startsWith('room-');
        const itemId = parseInt(itemKey.split('-')[1]);

        // Find original order based on room_id or common_space_id
        const originalOrder = orders.find(o =>
          isRoom ? o.room_id === itemId : o.common_space_id === itemId
        );

        if (mainOrder && mainOrder.is_temp) {
          // New order to create
          const orderData = {
            date: selectedDate,
            cleaning_type_id: mainOrder.cleaning_type_id,
            cleaning_staff_id: mainOrder.assigned_staff_id ? parseInt(mainOrder.assigned_staff_id) : null,
            cleaning_complete: mainOrder.cleaning_complete || false,
            notes: mainOrder.notes || ''
          };

          if (isRoom) {
            orderData.room_id = itemId;
            orderData.bed_disposition_id = mainOrder.bed_disposition_id;
            // Only rooms have checkout
            orderData.checkout_status = mainOrder.checkout_status || false;
          } else {
            orderData.common_space_id = itemId;
            // Common spaces don't have checkout
          }

          ordersToSave.push(orderData);
        } else if (mainOrder && originalOrder) {
          // Existing order to update
          const updateData = {
            id: originalOrder.id,
            cleaning_type_id: mainOrder.cleaning_type_id,
            cleaning_staff_id: mainOrder.assigned_staff_id ? parseInt(mainOrder.assigned_staff_id) : null,
            cleaning_status: mapCleaningStatusForBackend(mainOrder.cleaning_complete),
            notes: mainOrder.notes || ''
          };

          if (isRoom) {
            updateData.bed_disposition_id = mainOrder.bed_disposition_id;
            // Only rooms have checkout
            updateData.checkout_status = mapCheckoutStatusForBackend(mainOrder.checkout_status);
          }

          ordersToUpdate.push(updateData);
        } else if (!mainOrder && originalOrder) {
          // Order to delete
          ordersToDelete.push(originalOrder.id);
        }
      });

      // Execute API calls
      const promises = [];

      if (ordersToSave.length > 0) {
        promises.push(ordersAPI.bulkCreate(ordersToSave));
      }

      ordersToUpdate.forEach(order => {
        promises.push(ordersAPI.update(order.id, order));
      });

      ordersToDelete.forEach(orderId => {
        promises.push(ordersAPI.delete(orderId));
      });

      await Promise.all(promises);

      // Reload data
      await loadData();
      setTableHasChanges(false);

      addNotification({
        type: 'success',
        message: SUCCESS_MESSAGES.ORDER_UPDATED
      });

    } catch (error) {
      console.error('Error saving orders:', error);

      // Provide specific error message based on connection status
      const errorMessage = !isOnline
        ? 'No hay conexión a Internet. Por favor, verifica tu conexión e intenta de nuevo.'
        : 'Error al guardar las órdenes. Por favor, intenta de nuevo.';

      addNotification({
        type: 'error',
        message: errorMessage
      });
    } finally {
      setIsSaving(false);
    }
  };

  // Handle navigation warning confirmation
  const handleConfirmNavigation = () => {
    setTableHasChanges(false);
    setShowNavigationWarning(false);

    if (pendingNavigationPath) {
      // Navigate after a short delay to allow state to update
      setTimeout(() => {
        navigate(pendingNavigationPath);
        setPendingNavigationPath(null);
      }, 0);
    }
  };

  // Handle navigation warning cancellation
  const handleCancelNavigation = () => {
    setShowNavigationWarning(false);
    setPendingNavigationPath(null);
  };

  const getFilteredRooms = () => {
    let filtered = [...rooms];

    if (filters.showWithOrders !== null) {
      filtered = filtered.filter(room => {
        const hasOrders = (roomOrders[room.id] || []).length > 0;
        return filters.showWithOrders ? hasOrders : !hasOrders;
      });
    }

    return filtered.sort((a, b) => a.number.localeCompare(b.number));
  };

  const getOrdersSummary = () => {
    const totalRooms = rooms.length;
    const roomsWithOrders = Object.values(roomOrders).filter(orders => orders.length > 0).length;
    const roomsWithoutOrders = totalRooms - roomsWithOrders;

    let checkedOutRooms = 0;
    let cleanedRooms = 0;
    let completedOrders = 0;

    Object.values(roomOrders).forEach(roomOrdersList => {
      if (roomOrdersList.length > 0) {
        const mainOrder = roomOrdersList[0];
        if (mainOrder.checkout_status) checkedOutRooms++;
        if (mainOrder.cleaning_complete) cleanedRooms++;
        if (mainOrder.status === 'completed') completedOrders++;
      }
    });

    return {
      totalRooms,
      roomsWithOrders,
      roomsWithoutOrders,
      checkedOutRooms,
      cleanedRooms,
      completedOrders
    };
  };

  const getStaffSummary = () => {
    const summary = {};
    const cleaningStaff = getCleaningStaff();

    cleaningStaff.forEach(staff => {
      summary[staff.id] = {
        name: staff.name,
        totalOrders: 0,
        completedOrders: 0,
        pendingOrders: 0,
        orders: []
      };
    });

    Object.values(roomOrders).forEach(roomOrdersList => {
      roomOrdersList.forEach(order => {
        if (order.assigned_staff_id && summary[order.assigned_staff_id]) {
          summary[order.assigned_staff_id].totalOrders++;
          summary[order.assigned_staff_id].orders.push(order);

          if (order.status === 'completed') {
            summary[order.assigned_staff_id].completedOrders++;
          } else {
            summary[order.assigned_staff_id].pendingOrders++;
          }
        }
      });
    });

    return summary;
  };

  const filteredRooms = getFilteredRooms();
  const cleaningStaff = getCleaningStaff();
  const staffSummary = getStaffSummary();
  const ordersSummary = getOrdersSummary();

  return (
    <>
      {/* Overlay de carga - Se superpone sin mover el contenido */}
      {isRefreshing && (
        <div className="fixed inset-0 z-50 pointer-events-none flex items-start justify-center pt-20">
          <div className="bg-white rounded-lg shadow-xl p-4 flex items-center gap-3 border-2 border-blue-500 pointer-events-auto">
            <LoadingSpinner size="sm" />
            <span className="text-sm font-medium text-gray-900">Actualizando datos...</span>
          </div>
        </div>
      )}

      <div className="space-y-6">
      {/* Header - SIEMPRE VISIBLE */}
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-gray-900">
          Gestión de Órdenes
        </h1>
      </div>

      {/* Main content */}
      <div className="flex space-x-6">
        {/* Excel table con Skeleton */}
        <div className="flex-1">
          {isLoading ? (
            <SkeletonTable rows={15} columns={8} showActions animation="shimmer" duration={1.5} />
          ) : (
            <CleaningOrdersTable
              rooms={filteredRooms}
              commonSpaces={commonSpaces}
              roomOrders={roomOrders}
              cleaningStaff={cleaningStaff}
              onOrderChange={handleOrderChange}
              onOrderCreate={handleOrderCreate}
              onOrderDelete={handleOrderDelete}
            hasChanges={tableHasChanges}
            onSave={handleSaveOrders}
            isSaving={isSaving}
            selectedDate={selectedDate}
            onDateChange={handleDateChange}
            onPreviousDay={handlePreviousDay}
            onNextDay={handleNextDay}
            onCheckoutStatusChange={handleCheckoutStatusChange}
            onCleaningStatusChange={handleCleaningStatusChange}
            establishmentId={user?.establishment_id}
          />
          )}
        </div>
      </div>

      {/* Navigation Warning Dialog */}
      {showNavigationWarning && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl p-6 max-w-md w-full mx-4">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">
              Cambios sin guardar
            </h3>
            <p className="text-gray-700 mb-6">
              Tienes cambios sin guardar en las órdenes de limpieza. ¿Deseas continuar sin guardar?
            </p>
            <div className="flex justify-end space-x-3">
              <button
                onClick={handleCancelNavigation}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-md transition-colors"
              >
                Cancelar
              </button>
              <button
                onClick={handleConfirmNavigation}
                className="px-4 py-2 text-sm font-medium text-white bg-red-600 hover:bg-red-700 rounded-md transition-colors"
              >
                Salir sin guardar
              </button>
            </div>
          </div>
        </div>
      )}
      </div>
    </>
  );
};

export default CleaningOrdersDesktop;